// src/pages/layouts/dashboard/sections/PartyCrest.tsx
import React from 'react';
import Typography from 'core/components/Typography';
import ImageSlot from 'core/components/ImageSlot';
import { crestPrefix } from 'core/services/firebase/storage/ImageStorageService';
import { useGroups } from 'features/user-management';
import ImageUploadControl from 'shared/components/ImageUploadControl';
import { useImageAttachment } from 'shared/hooks/useImageAttachment';
import { useCampaignInfo } from '../../common/hooks/useCampaignInfo';
import { defaultCrestFor } from './defaultPictures';

interface PartyCrestProps {
  /** Player count, shown in the summary line when the group has members. */
  memberCount?: number;
  /** Chapter count, shown alongside it. */
  chapterCount?: number;
}

/**
 * The party's own card, at the foot of the aside.
 *
 * It carries the group's crest. Most groups will have none for most of their
 * life, so until one is uploaded the slot shows an old print of a coat of arms
 * instead (T074), picked by the group's id so a group always gets the same
 * one. It is shown, never stored, and the slot's accessible name still says
 * that no crest has been uploaded.
 *
 * Every member sees the crest; only group admins may change it, matching who
 * may edit the group document (and `storage.rules.prod`).
 *
 * This identifies the group -- who is at the table -- which is why it sits here
 * and not in the hero. The hero names the campaign; a second monogram up there
 * only repeated the campaign title's first letter back at it.
 */
const PartyCrest: React.FC<PartyCrestProps> = ({ memberCount, chapterCount }) => {
  const { activeGroup, hasGroup } = useCampaignInfo();
  const { activeGroupId, isAdmin, setGroupCrest } = useGroups();

  const crest = useImageAttachment({
    prefix: activeGroupId ? crestPrefix(activeGroupId) : null,
    current: activeGroup?.crest,
    save: setGroupCrest,
  });

  if (!hasGroup || !activeGroup?.name) return null;

  const summary = [
    memberCount ? `${memberCount} ${memberCount === 1 ? 'player' : 'players'}` : null,
    chapterCount ? `${chapterCount} ${chapterCount === 1 ? 'chapter' : 'chapters'}` : null,
  ].filter(Boolean);

  // Rounded here rather than clipped by the card: the compact control's
  // progress and error note hangs below the picture, and the card's
  // `overflow-hidden` would cut it off when the text under the name is short.
  const slot = (
    <ImageSlot
      className="h-28 rounded-t-lg"
      label={`${activeGroup.name} crest — none uploaded yet`}
      image={activeGroup.crest}
      alt={`${activeGroup.name} crest`}
      fallback={defaultCrestFor(activeGroup.id)}
    />
  );

  return (
    <div className="rounded-lg card" data-testid="party-crest">
      {isAdmin ? (
        <ImageUploadControl
          variant="compact"
          subject="crest"
          hasImage={Boolean(activeGroup.crest)}
          onUpload={crest.upload}
          onRemove={crest.remove}
        >
          {slot}
        </ImageUploadControl>
      ) : (
        slot
      )}
      <div className="px-5 py-4">
        <Typography variant="h4" className="text-base">
          {activeGroup.name}
        </Typography>
        {summary.length > 0 && (
          <Typography variant="body-sm" color="muted" className="text-xs mt-1">
            {summary.join(' · ')}
          </Typography>
        )}
      </div>
    </div>
  );
};

export default PartyCrest;
